// src/services/tenantService.js
/**
 * @file tenantService.js
 * @description Service for handling tenant-related API operations, including fetching tenant lists,
 * overview metrics, and detailed user metrics for the Estrel SaaS platform.
 */
import api from "./apiClient";
import { fetchDashboardKpis } from "./analyticsService";

// Base URLs
const DETAILED_API_BASE = "https://utility.wiiz.it/api/admin/kpi/user";

// Scopes
const SCOPES = [
  { id: "estrelSaas", label: "Estrel SaaS", disabled: false },
  { id: "education", label: "Education", disabled: true },
  { id: "enterprise", label: "Enterprise Tenants", disabled: true },
];

/**
 * Fetches tenant overview data for the dashboard.
 * 
 * @param {string} timePeriod - The time filter to apply (e.g., 'weekly', 'monthly').
 * @param {string|null} planFilter - Optional plan filter (e.g., 'starter', 'enterprise').
 * @returns {Promise<Object>} Formatted tenant data including charts and stats.
 */
export async function fetchTenantOverview(timePeriod = "all_time", customRange = null, planFilter = null, forceRefresh = false) {
  // Use Dashboard API for the top-level charts on Tenant Page
  const apiData = await fetchDashboardKpis(timePeriod, customRange, planFilter, forceRefresh);

  if (!apiData) return getDefaultMock();

  const summary = apiData.platform_summary || {};
  const history = apiData.execution_history || [];
  const moduleUsage = apiData.module_usage_distribution || {};
  const plans = summary.plan_distribution || {};

  // 1. Plan Distribution (Bar Chart)
  // Filter out 0 values to clean up chart, capitalizes keys
  const planEntries = Object.entries(plans).filter(([_, val]) => val > 0);
  const planLabels = planEntries.map(([k]) => k.charAt(0).toUpperCase() + k.slice(1));
  const planValues = planEntries.map(([_, val]) => val);

  const planSeries = [{ name: "Users", data: planValues }];

  // 2. Execution Trend (Line Chart)
  // If history is missing/empty, create dummy dates for flat line
  let trendLabels = [];
  let trendValues = [];

  if (history.length > 0) {
    trendLabels = history.map(h => h.date);
    trendValues = history.map(h => h.total_executions || h.count);
  } else {
    // Fallback if no history provided in JSON
    trendLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    trendValues = [0, 0, 0, 0, 0, 0, 0];
  }

  const trendSeries = [{ name: "Executions", data: trendValues }];

  // 3. Module Usage (Donut Chart)
  // Filter out 0 values so the legend only shows active modules
  const moduleEntries = Object.entries(moduleUsage).filter(([_, val]) => (val?.count || 0) > 0);
  const moduleLabels = moduleEntries.map(([k]) => k);
  const moduleValues = moduleEntries.map(([_, val]) => val?.count || 0);

  return {
    activeScope: "estrelSaas",
    scopes: SCOPES,
    charts: {
      usersByPlan: {
        labels: planLabels,
        series: planSeries
      },
      executionTrend: {
        categories: trendLabels,
        series: trendSeries
      },
      moduleUsage: {
        labels: moduleLabels,
        series: moduleValues
      }
    }
  };
}

/**
 * Retrieves the list of tenants formatted for the management table.
 * 
 * @param {string} type - The scope type (e.g., 'estrelSaas').
 * @param {string} timePeriod - Time period for metric aggregation.
 * @returns {Promise<Array>} List of tenant objects with standardized properties.
 */
export async function getTenantList(type, timePeriod = "all_time", customRange = null, forceRefresh = false) {
  if (type === "estrelSaas") {
    try {
      const data = await fetchDashboardKpis(timePeriod, customRange, null, forceRefresh);
      if (data && data.admins) {
        return data.admins.map(admin => ({
          id: admin.admin_info?.org_id,
          name: admin.admin_info?.display_name || admin.admin_info?.username,
          username: admin.admin_info?.username,
          email: admin.admin_info?.email,
          plan: admin.admin_info?.plan,
          subscriptionStartDate: admin.subscription_info?.subscription_start_date || "-",
          subscriptionEndDate: admin.subscription_info?.subscription_end_date || "-",
          executions: 0,
          storageGB: 0,
          usersCount: admin.user_management?.total_created_users || 0,
          raw: admin
        }));
      }
    } catch (err) {
      console.error("Failed to fetch SaaS tenants", err);
    }
  }
  return [];
}

/**
 * Fetches detailed metrics for a specific user/tenant.
 * 
 * @param {string} username - The username identifier for the tenant.
 * @param {string} timePeriod - Time period for the detailed metrics.
 * @param {Object|null} customRange - Custom date range object {start, end} if timePeriod is 'custom'.
 * @returns {Promise<Object>} Detailed metrics data object.
 */
/**
 * Fetches detailed metrics for a specific user/tenant.
 * 
 * @param {string} username - The username identifier for the tenant.
 * @param {string} timePeriod - Time period for the detailed metrics.
 * @param {Object|null} customRange - Custom date range object {start, end} if timePeriod is 'custom'.
 * @returns {Promise<Object>} Detailed metrics data object.
 */
export async function fetchUserDetailedMetrics(username, timePeriod = "monthly", customRange = null) {
  try {
    let query = `time_period=${timePeriod}`;
    if (timePeriod === "custom" && customRange?.start && customRange?.end) {
      query += `&start_date=${customRange.start}&end_date=${customRange.end}`;
    }
    const url = `${DETAILED_API_BASE}/${username}/detailed?${query}`;
    console.log("Fetching details for:", username);
    const response = await api.get(url);
    if (response.data && response.data.status === "success") {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error(`Failed to fetch details for ${username}:`, error);
    return null;
  }
}

function getDefaultMock() {
  return {
    scopes: SCOPES,
    charts: {
      usersByPlan: { labels: [], series: [] },
      executionTrend: { categories: [], series: [] },
      moduleUsage: { labels: [], series: [] }
    }
  };
}

export async function updateTenantPlan(tenantId, planId, limits) { return { success: true }; }
export async function updateEnterpriseProvisioningConfig(tenantId, config) { return { success: true }; }
export async function createEnterpriseTenant(tenantData) { return { success: true }; }